import { makeStyles } from '@material-ui/core'
import { GetStaticProps, GetStaticPaths } from 'next'
import React from 'react'
import { getFundsSlugs } from '../../lib/routeHelpers'

import Page from '@aletheia/common/components/Page'

import FundShow from '../../components/FundPage'
import { InstitutionalFundingData } from '../../components/Statistics/InstitutionalFunding'
import { PayoutChartDataPoint } from '../../components/Statistics/PayoutsByFund'
import {
  IFund,
  IPayoutReport,
  fetchFund,
  fetchPayoutReportsByFund,
} from '../../contentful'
import { grantsDB, payoutStats } from '../../lib/grantsDB'
import { institutionalFundingStats } from '../../lib/institutionalFunding'
import { Grant, fundSlugs } from '../grants'

type FundSingleProps = {
  Fund: IFund
  PayoutReports: IPayoutReport[]
  payoutChartData: PayoutChartDataPoint[]
  fundingSourceChartData: InstitutionalFundingData
  highlightedGrants: Grant[]
}

const useStyles = makeStyles((theme) => ({
  titleOverride: {
    [theme.breakpoints.down('sm')]: { fontSize: '2.5rem' },
    fontSize: '3.5rem',
  },
}))

const FundSingle: React.FC<FundSingleProps> = ({
  Fund,
  PayoutReports,
  payoutChartData,
  fundingSourceChartData,
  highlightedGrants,
}) => {
  const classes = useStyles()
  const { title, bannerImage } = Fund.fields
  const displayTitle = /Fund$/.test(title) ? title : `${title} Fund`
  return (
    <Page
      title={displayTitle}
      titleClass={classes.titleOverride}
      banner={bannerImage}
      variant="wide"
      breadcrumbsBase={{ href: '/funds', label: 'Funds' }}
    >
      <FundShow
        Fund={Fund.fields}
        PayoutReports={PayoutReports}
        payoutChartData={payoutChartData}
        fundingSourceChartData={fundingSourceChartData}
        highlightedGrants={highlightedGrants}
      />
    </Page>
  )
}

export default FundSingle

export const getStaticProps: GetStaticProps<
  FundSingleProps | { Fund: undefined; PayoutReports: undefined },
  { slug: string }
> = async ({ params }) => {
  if (!params?.slug) throw new Error('Missing slug')
  const Fund = await fetchFund(params.slug)
  const PayoutReports = await fetchPayoutReportsByFund(params.slug)
  if (!Fund) {
    return {
      notFound: true,
    }
  }

  const payoutChartData = await payoutStats()
  const fundingSourceChartData = await institutionalFundingStats()

  const allGrants = await grantsDB()
  const highlightedGrants = allGrants.filter((grant) => {
    return fundSlugs[grant.fund] === Fund.fields.slug && grant.highlighted
  })

  return {
    props: {
      Fund,
      PayoutReports,
      payoutChartData,
      fundingSourceChartData,
      highlightedGrants,
    },
    revalidate: 20,
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = await getFundsSlugs()
  return {
    paths: slugs.map((slug) => ({ params: { slug } })),
    fallback: 'blocking',
  }
}
